import {Injectable} from '@angular/core';
import OverlayModel from '../../../models/overlay.model';
import {OverlayType} from '../../../enums/overlay-type.enum';
import {PositionInterface} from '../interfaces/position.interface';
import StyleModel from '../../../models/style.model';
import {ImageSizeAdjustment} from '../../../enums/image-size-adjustment.enum';

@Injectable({
    providedIn: 'root'
})
export class OverlayService {

    getStyle(overlay: OverlayModel, position: PositionInterface, isHover: boolean = false, isVisible: boolean = false, useFallbackImage: boolean = false): any {
        const style: any = overlay.style || {
        };

        if (overlay.font) {
            style.fontSize = overlay.font.size + 'rem';
            style.fontFamily = overlay.font.family;
            style.color = overlay.font.color;
        }

        if (overlay.backgroundColor) {
            style.backgroundColor = overlay.backgroundColor;
        }

        if (overlay.size) {
            style.width = overlay.size.width + '%';
            style.height = overlay.size.height + '%';
        }

        if (overlay.position) {
            const isInvisibleImageOverlay = overlay.type === 'image' && !isVisible;
            style.left = isInvisibleImageOverlay ? '100%' : (position.x + '%');
            style.top = isInvisibleImageOverlay ? '100%' : (position.y + '%');
        }

        if (overlay.style.borderRadius) {
            style.borderRadius = this.toRem(overlay.style.borderRadius);
        }

        if (overlay.type === OverlayType.Image) {
            style.backgroundImage = `url("${this.getImageUrl(overlay, useFallbackImage)}")`;
            style.backgroundSize = this.getSizeStyle(overlay);
            style.outline = 0;
        }
        if (overlay.type === OverlayType.Submit) {
            style.zIndex = 100;
        }

        if (isHover) {
            if (overlay.type === OverlayType.Button) {
                style.color = overlay.font.hoverColor ? overlay.font.hoverColor : overlay.font.color;
                style.backgroundColor = overlay.backgroundHoverColor ? overlay.backgroundHoverColor : overlay.backgroundColor;
            }

            if (overlay.navigateTo && overlay.type === OverlayType.Image) {
                style.outline = `${overlay.borderHoverSize}px solid ${overlay.borderHoverColor}`;
                style.cursor = 'pointer';
            }
        }

        return style;
    }

    toRem(size: string): string {
        const parsed = size.match(/^(\d+(\.\d+)?)([a-z%]+)$/) || [];

        if (parsed[3] === 'px') {
            return (+parsed[1] / 14) + 'rem';
        }

        return size;
    }

    isDisplayed(currentTime: number, overlay: OverlayModel): boolean {
        return currentTime >= overlay.time.start && currentTime < overlay.time.end;
    }

    isStable(currentTime: number, overlay: OverlayModel): boolean {
        const animationStart = overlay.animations?.start.duration ?? 0;
        const transitionStart = overlay.transition?.start.duration ?? 0;
        const totalOverlayStartTime = overlay.time.start + animationStart + transitionStart;

        return currentTime > totalOverlayStartTime;
    }

    getCurrentOpacity(currentFrame, overlay: OverlayModel) {
        let opacityObject;

        if (overlay.hasOwnProperty('transition')) {
            if (overlay.transition.frames.animationIn[currentFrame] === 0) {
                return 0;
            }

            opacityObject = overlay.transition.frames.animationIn[currentFrame]
        || overlay.transition.frames.animationOut[currentFrame];
        }

        return opacityObject !== undefined
            ? opacityObject
            : overlay.style.opacity;
    }

    getCurrentPosition(currentFrame, overlay: OverlayModel) {
        const position = {
            x: 0, y: 0
        };

        if (overlay.position) {
            position.x = overlay.position.left;
            position.y = overlay.position.top;
        }

        if (overlay.hasOwnProperty('animations')) {
            return overlay.animations.frames.animationIn[currentFrame] ||
             overlay.animations.frames.animationOut[currentFrame] ||
             position;
        }

        return position;
    }

    addTimeToLastSceneOverlay(overlay, totalTime) {
        if (overlay.time && overlay.time.end === totalTime) {
            overlay.time.end += 1;
        }

        return;
    }

    getImageUrl(overlay: OverlayModel, useFallbackImage: boolean = false): string {
        if (
            overlay.isPersonalized &&
            /https:\/\/ui\..*\.engageone\.video\/assets\/images\/img\-placeholder\.png/.test(overlay.image)
        ) {
            return overlay.personalizedUrl;
        }

        if (overlay.isPersonalized && overlay.personalizedUrl && !useFallbackImage) {
            return overlay.personalizedUrl;
        }

        if (overlay.image.includes('eocm-client-data')) {
            return overlay.image.replace(/^(https?:\/\/)([^.]+)\.s3(?:\.[^.]+)?\.amazonaws\.com(\/.*)?$/,
                '$1$2.s3-accelerate.amazonaws.com$3')
        }

        return overlay.image;
    }

    private getSizeStyle(overlay: OverlayModel): string {
        if (!overlay.isPersonalized) {
            return 'cover';
        }

        switch (overlay.sizeAdjustment) {
            case ImageSizeAdjustment.HEIGHT:
                return 'auto 100%';
            case ImageSizeAdjustment.WIDTH:
                return 'cover';
            case ImageSizeAdjustment.NONE:
                return 'contain';
            default:
                return 'cover';
        }
    }
}
